import { createLogger } from "@/lib/logger";
import { ParticipantKind, type RemoteParticipant, Room, RoomEvent } from "livekit-client";
import { z } from "zod";
import { WhiteboardUpdateOpSchema } from "../spec";
import type { WhiteboardSpec } from "../spec";
import { STREAM_CAPABILITY, STREAM_EVENTS, STREAM_TOPIC, StreamController } from "./controller";

const logger = createLogger("StreamingWhiteboard");
const frameSchema = z
  .object({
    type: z.enum(["begin", "record", "complete", "failed", "cancel", "cancelled", "annotation"]),
    session: z.string().regex(/^[a-f0-9]{32}$/),
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-zA-Z0-9_-]+$/),
    revision: z.literal(1),
    request: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    sequence: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    record: z.string().max(8192).optional(),
    exercise: z.string().max(48).optional(),
    operations: z.array(WhiteboardUpdateOpSchema).max(6).optional(),
    annotation_revision: z.number().int().positive().max(24).optional(),
    composition: z.boolean().optional(),
  })
  .strict();
export const streamControllers = new Map<string, StreamController>();
export const streamKey = (reference: { session: string; id: string }) =>
  `${reference.session}:${reference.id}`;

export function connectStreamingRoom(
  room: Room,
  show: (spec: WhiteboardSpec) => void,
  checked: (event: { result: string; resolved: boolean; countsAsAttempt?: boolean }) => void
) {
  let closed = false;
  let current:
    | {
        reference: { session: string; id: string; revision: number };
        request: number;
        sender: string;
        sequence: number;
        event: number;
        controller: StreamController;
        shown: boolean;
      }
    | undefined;
  let frame: number | undefined;
  const pending: Uint8Array[] = [];
  const send = (type: string, values: Record<string, unknown> = {}) => {
    if (closed || !current) return;
    void room.localParticipant
      .publishData(
        new TextEncoder().encode(
          JSON.stringify({
            ...current.reference,
            type,
            sequence: ++current.event,
            ...values,
          })
        ),
        { reliable: true, topic: STREAM_EVENTS, destinationIdentities: [current.sender] }
      )
      .catch(() => logger.warn("Stream acknowledgement unavailable", { type }));
  };
  const flush = () => {
    frame = undefined;
    const batch = pending.splice(0);
    for (const bytes of batch) current?.controller.push(bytes);
    if (
      current &&
      !current.shown &&
      Object.keys(current.controller.snapshot.spec.elements).length > 1
    ) {
      current.shown = true;
      show({
        kind: "composed",
        id: current.reference.id,
        title: "Language practice",
        streaming: current.reference,
        blocks: [{ type: "text", content: "Language practice" }],
      });
    }
  };
  const handle = (
    bytes: Uint8Array,
    participant?: RemoteParticipant,
    _kind?: unknown,
    topic?: string
  ) => {
    if (
      closed ||
      topic !== STREAM_TOPIC ||
      bytes.length > 10000 ||
      participant?.kind !== ParticipantKind.AGENT ||
      !["main3live", "main3", "main3geminicode", "main3geminiboard"].includes(
        participant.attributes["chickytutor.runtime"]
      )
    )
      return;
    const parsed = frameSchema.safeParse(
      (() => {
        try {
          return JSON.parse(new TextDecoder().decode(bytes));
        } catch {
          return null;
        }
      })()
    );
    if (!parsed.success) return;
    const message = parsed.data;
    if (message.type === "begin") {
      if (
        message.sequence !== 0 ||
        message.record ||
        (current &&
          (current.sender !== participant.identity ||
            current.reference.session !== message.session ||
            message.request <= current.request))
      )
        return;
      if (frame !== undefined) cancelAnimationFrame(frame);
      pending.length = 0;
      current?.controller.cancel();
      const controller = new StreamController(
        "progressive",
        undefined,
        message.composition === true
      );
      current = {
        reference: { session: message.session, id: message.id, revision: 1 },
        request: message.request,
        sender: participant.identity,
        sequence: 0,
        event: 0,
        controller,
        shown: false,
      };
      streamControllers.set(streamKey(current.reference), controller);
      while (streamControllers.size > 8) {
        const key = streamControllers.keys().next().value!;
        streamControllers.get(key)?.close();
        streamControllers.delete(key);
      }
      controller.onDisplayed = () => {
        if (current?.controller === controller)
          send("displayed", {
            ready: controller.snapshot.ready,
            elements: Object.keys(controller.snapshot.spec.elements),
            annotations: controller.snapshot.annotations,
            revealed: Object.keys(controller.snapshot.revealed).filter(
              (id) => controller.snapshot.revealed[id]
            ),
          });
      };
      controller.onNativeEvent = (exercise, detail) => {
        if (current?.controller !== controller) return;
        send("native_event", { exercise, detail });
        if (detail.event === "reading_complete") {
          checked({ result: "read", resolved: true, countsAsAttempt: false });
        }
        if (["answered", "completed"].includes(detail.event)) {
          const snapshot = controller.snapshot;
          checked({
            result:
              detail.correct === true
                ? "correct"
                : detail.correct === false
                  ? "incorrect"
                  : "completed",
            resolved:
              snapshot.phase === "complete" &&
              snapshot.ready.every((id) =>
                ["correct", "completed", "read", "observed"].includes(snapshot.results[id])
              ),
          });
        }
      };
      controller.onCheck = (event) => {
        if (current?.controller === controller) {
          send("check", event);
          checked(event);
        }
      };
      controller.onClosed = () => {
        if (current?.controller === controller) send("closed");
      };
      controller.onHidden = () => {
        if (current?.controller === controller) send("hidden");
      };
      return;
    }
    if (
      !current ||
      participant.identity !== current.sender ||
      message.session !== current.reference.session ||
      message.id !== current.reference.id ||
      message.request !== current.request
    )
      return;
    if (message.sequence <= current.sequence) return;
    if (message.sequence !== current.sequence + 1) {
      send("resume");
      return;
    }
    current.sequence = message.sequence;
    if (message.type === "record" && message.record !== undefined) {
      pending.push(new TextEncoder().encode(message.record + "\n"));
      if (frame === undefined) frame = requestAnimationFrame(flush);
    } else {
      if (frame !== undefined) cancelAnimationFrame(frame);
      flush();
      if (
        message.type === "annotation" &&
        message.exercise &&
        message.operations &&
        message.annotation_revision
      ) {
        current.controller.annotate(
          message.exercise,
          message.operations,
          message.annotation_revision
        );
      } else if (message.type === "complete") {
        current.controller.finish();
        const snapshot = current.controller.snapshot;
        if (
          snapshot.phase === "complete" &&
          snapshot.ready.length &&
          snapshot.ready.every((id) =>
            ["correct", "completed", "read", "observed"].includes(snapshot.results[id])
          )
        )
          checked({
            result: snapshot.ready.some((id) => snapshot.results[id] === "correct")
              ? "correct"
              : "completed",
            resolved: true,
            countsAsAttempt: false,
          });
      } else if (message.type === "failed") current.controller.fail();
      else current.controller.cancel();
    }
  };
  const advertise = () => {
    if (closed) return;
    void room.localParticipant
      .setAttributes({ [STREAM_CAPABILITY]: "2" })
      .catch(() => logger.warn("Streaming capability unavailable"));
  };
  const resumed = () => {
    advertise();
    send("resume");
  };
  room.on(RoomEvent.DataReceived, handle);
  room.on(RoomEvent.Connected, advertise);
  room.on(RoomEvent.Reconnected, resumed);
  advertise();
  return () => {
    closed = true;
    if (frame !== undefined) cancelAnimationFrame(frame);
    pending.length = 0;
    current?.controller.cancel();
    if (current) {
      current.controller.onCheck = undefined;
      current.controller.onNativeEvent = undefined;
      current.controller.onDisplayed = undefined;
      current.controller.onClosed = undefined;
      current.controller.onHidden = undefined;
    }
    room.off(RoomEvent.DataReceived, handle);
    room.off(RoomEvent.Connected, advertise);
    room.off(RoomEvent.Reconnected, resumed);
  };
}
